import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, XIcon, DropletsIcon } from "lucide-react";

import { trpc } from "@/hooks/trpc";
import type {
  FermentationLineTypeId,
  FermentationProcedureId,
  FermentationProcedureLineId,
  InvResourceId,
} from "@brewbase/db/types";
import { FermLineDriveEnum } from "@brewbase/db/types";
import {
  LINE_DRIVE_LABELS,
  LINE_DRIVE_DESCRIPTIONS,
  isStatDrive,
  statSetpointCodeForDrive,
  type LineDrive,
  UNIT_CATEGORIES,
  feedWeightUnitPrefKey,
  resolvePreferences,
} from "@brewbase/lib";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { NumericInput } from "@/components/ui/numeric-input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemDescription,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResourceSelect } from "@/features/inventory/resource-select";
import { lineColorVar } from "@/lib/line-colors";
import type { ProcedureWithDetails } from "@/features/fermentation-procedure-editor/types";

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/lines",
)({
  component: LinesPage,
});

type ProcedureLine = ProcedureWithDetails["lines"][number];
type LineType = {
  id: FermentationLineTypeId;
  code: string;
  name: string;
  description: string | null;
  default_drive: FermLineDriveEnum;
  has_concentration: boolean;
  feed_resource_id: InvResourceId | null;
  feed_resource_category_id: number | null;
  color: string | null;
};

const DRIVE_ORDER: FermLineDriveEnum[] = [
  FermLineDriveEnum.RATE,
  FermLineDriveEnum.PULSE,
  FermLineDriveEnum.PH_STAT,
  FermLineDriveEnum.DO_STAT,
];

function LinesPage() {
  const { procedure_id } = useParams({ from: Route.id });
  const procedureId = procedure_id as unknown as FermentationProcedureId;
  const utils = trpc.useUtils();

  const [selectedId, setSelectedId] =
    useState<FermentationProcedureLineId | null>(null);

  const { data: procedure, isLoading } =
    trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);
  const { data: lineTypes } = trpc.fermentationLineTypes.list.useQuery();

  const invalidate = () =>
    utils.fermentationProcedures.getWithDetails.invalidate(procedureId);

  const addLine = trpc.fermentationProcedures.addLine.useMutation({
    onSuccess: (line) => {
      toast.success(`${line.lineTypeName} added`);
      setSelectedId(line.id);
      invalidate();
    },
    onError: (error) => toast.error(`Failed to add line: ${error.message}`),
  });

  const updateLine = trpc.fermentationProcedures.updateLine.useMutation({
    onSuccess: () => invalidate(),
    onError: (error) => toast.error(`Failed to update line: ${error.message}`),
  });

  const removeLine = trpc.fermentationProcedures.removeLine.useMutation({
    onSuccess: () => {
      toast.success("Line removed");
      setSelectedId(null);
      invalidate();
    },
    onError: (error) => toast.error(`Failed to remove line: ${error.message}`),
  });

  // Unit preferences live on the procedure as one JSON blob; a line's weight
  // unit is one key in it, so the write goes through the procedure update.
  const updateProcedure = trpc.fermentationProcedures.update.useMutation({
    onSuccess: () => invalidate(),
    onError: (error) => toast.error(`Failed to save unit: ${error.message}`),
  });

  const unitPrefs = useMemo(
    () => (procedure?.unit_preferences as Record<string, string>) ?? {},
    [procedure?.unit_preferences],
  );

  const setLineWeightUnit = (lineTypeCode: string, unit: string | null) => {
    const updated = { ...unitPrefs };
    const key = feedWeightUnitPrefKey(lineTypeCode);
    if (unit === null) delete updated[key];
    else updated[key] = unit;
    updateProcedure.mutate({ id: procedureId, unitPreferences: updated });
  };

  const reorderLines = trpc.fermentationProcedures.reorderLines.useMutation({
    onSuccess: () => invalidate(),
    onError: (error) => {
      toast.error(`Failed to reorder lines: ${error.message}`);
      invalidate();
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // The service already orders by display_order then id; sort defensively so
  // the list is stable whatever the cache holds mid-reorder.
  const lines = useMemo(() => {
    if (!procedure?.lines) return [];
    return [...procedure.lines].sort((a, b) => {
      const byOrder = a.display_order - b.display_order;
      return byOrder !== 0 ? byOrder : (a.id as number) - (b.id as number);
    });
  }, [procedure?.lines]);

  const lineTypesById = useMemo(() => {
    const map = new Map<number, LineType>();
    for (const lt of lineTypes ?? []) map.set(lt.id, lt);
    return map;
  }, [lineTypes]);

  // A line type can be declared once per procedure (partial unique index), so
  // only undeclared types are offered.
  const availableLineTypes = useMemo(() => {
    const declared = new Set(lines.map((l) => l.line_type_id as number));
    return (lineTypes ?? []).filter((lt) => !declared.has(lt.id as number));
  }, [lineTypes, lines]);

  const selectedLine = useMemo(
    () => lines.find((l) => l.id === selectedId) ?? lines[0] ?? null,
    [lines, selectedId],
  );

  const isLocked = procedure?.is_locked ?? false;

  const handleAdd = (lineTypeId: FermentationLineTypeId) => {
    if (isLocked) return;
    const lineType = lineTypesById.get(lineTypeId as number);
    addLine.mutate({
      procedureId,
      lineTypeId,
      // The registry's default drive is the design's starting point.
      drive: lineType?.default_drive ?? FermLineDriveEnum.RATE,
      defaultResourceId: lineType?.feed_resource_id ?? null,
    });
  };

  const handleUpdate = (updates: {
    drive?: FermLineDriveEnum;
    defaultResourceId?: InvResourceId | null;
    defaultConcentrationPct?: string | null;
    isOptional?: boolean;
    notes?: string | null;
  }) => {
    if (isLocked || !selectedLine) return;
    updateLine.mutate({ procedureId, lineId: selectedLine.id, ...updates });
  };

  const handleRemove = (lineId: FermentationProcedureLineId) => {
    if (isLocked) return;
    removeLine.mutate({ procedureId, lineId });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isLocked) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = lines.findIndex((l) => l.id.toString() === active.id);
    const newIndex = lines.findIndex((l) => l.id.toString() === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...lines];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Reflect the move immediately; reorderLines writes the same indices
    // server-side and the invalidate that follows confirms them.
    utils.fermentationProcedures.getWithDetails.setData(procedureId, (old) =>
      old
        ? {
            ...old,
            lines: reordered.map((l, idx) => ({ ...l, display_order: idx })),
          }
        : old,
    );

    reorderLines.mutate({
      procedureId,
      orderedIds: reordered.map((l) => l.id),
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3 h-full">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!procedure) {
    return <div className="p-4 text-sm">Procedure not found</div>;
  }

  const manageLink = (
    <Link
      to="/settings/fermentation-parameters"
      search={{ tab: "lines" }}
      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
    >
      Manage lines
    </Link>
  );

  const addSelect =
    !isLocked && availableLineTypes.length > 0 ? (
      <Select
        value=""
        onValueChange={(value) =>
          handleAdd(Number(value) as unknown as FermentationLineTypeId)
        }
        disabled={addLine.isPending}
      >
        <SelectTrigger className="h-7 w-48 text-xs">
          <SelectValue placeholder="Add line…" />
        </SelectTrigger>
        <SelectContent>
          {availableLineTypes.map((lt) => (
            <SelectItem key={lt.id} value={lt.id.toString()}>
              {lt.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : null;

  // The registry link sits with the picker: both are about which lines exist
  // rather than how the selected one is configured.
  const addControl = (
    <div className="flex items-center gap-3">
      {addSelect}
      {manageLink}
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-4 py-2 border-b flex-shrink-0">
        <div className="text-sm font-medium">Lines</div>
        <div className="text-xs text-muted-foreground flex-1">
          What addition lines are connected to the fermentation - not
          neccessarily all active
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-muted-foreground max-w-sm">
            <p className="text-sm mb-1">No lines declared.</p>
            <p className="text-xs mb-3">
              Without a declared line a fermentation on this procedure cannot
              record a feed on it.
            </p>
            {addControl}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-5 min-h-0 border-r">
            <ScrollArea className="h-full">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={lines.map((l) => l.id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="grid grid-cols-[24px_1fr_auto_28px] gap-1 px-2 py-1.5 text-xs font-medium text-muted-foreground border-b bg-muted/30 sticky top-0">
                    <div></div>
                    <div>Line</div>
                    <div className="text-center">Drive</div>
                    <div></div>
                  </div>
                  <div>
                    {lines.map((line) => (
                      <LineTableRow
                        key={line.id}
                        line={line}
                        selected={selectedLine?.id === line.id}
                        onClick={() => setSelectedId(line.id)}
                        onDelete={() => handleRemove(line.id)}
                        readOnly={isLocked}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <div className="p-2">{addControl}</div>
            </ScrollArea>
          </div>

          <div className="md:col-span-7 min-h-0">
            {selectedLine ? (
              <LineInspector
                key={selectedLine.id}
                line={selectedLine}
                lineType={lineTypesById.get(selectedLine.line_type_id as number)}
                onUpdate={handleUpdate}
                weightUnit={
                  unitPrefs[feedWeightUnitPrefKey(selectedLine.lineTypeCode)] ??
                  null
                }
                procedureWeightUnit={
                  resolvePreferences(unitPrefs).additionWeight
                }
                onWeightUnitChange={(unit) =>
                  setLineWeightUnit(selectedLine.lineTypeCode, unit)
                }
                isLocked={isLocked}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground bg-muted/20">
                Select a line to edit.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ======================== Line Table Row ========================

function LineTableRow({
  line,
  selected,
  onClick,
  onDelete,
  readOnly,
}: {
  line: ProcedureLine;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  readOnly: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: line.id.toString(), disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : "auto",
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "grid grid-cols-[24px_1fr_auto_28px] gap-1 items-center px-2 py-1.5 text-sm hover:bg-muted/50 cursor-pointer border-b border-border/50 " +
        (selected ? "bg-primary/5 border-l-2 border-l-primary" : "")
      }
      onClick={onClick}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground flex-shrink-0 p-0.5"
        aria-label="Drag to reorder"
      >
        <GripVerticalIcon className="h-4 w-4" />
      </button>

      <div className="min-w-0 flex items-center gap-1.5">
        {line.lineTypeColor ? (
          <span
            className="h-2 w-2 flex-shrink-0"
            style={{
              backgroundColor: lineColorVar(line.lineTypeColor, 9),
            }}
          />
        ) : (
          <DropletsIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="truncate font-medium">{line.lineTypeName}</span>
        {line.is_optional && (
          <span className="text-xs bg-muted text-muted-foreground px-1 py-0.5 flex-shrink-0">
            Optional
          </span>
        )}
      </div>

      <div className="text-center text-xs text-muted-foreground">
        {LINE_DRIVE_LABELS[line.drive as LineDrive]}
      </div>

      {readOnly ? (
        <div />
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="h-5 w-5 p-0 hover:text-destructive hover:bg-destructive/10"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ======================== Line Inspector ========================

function LineInspector({
  line,
  lineType,
  onUpdate,
  weightUnit,
  procedureWeightUnit,
  onWeightUnitChange,
  isLocked,
}: {
  line: ProcedureLine;
  lineType: LineType | undefined;
  onUpdate: (updates: {
    drive?: FermLineDriveEnum;
    defaultResourceId?: InvResourceId | null;
    defaultConcentrationPct?: string | null;
    isOptional?: boolean;
    notes?: string | null;
  }) => void;
  /** The line's own override, null when it follows the procedure. */
  weightUnit: string | null;
  /** What the procedure's addition weight preference resolves to. */
  procedureWeightUnit: string;
  onWeightUnitChange: (unit: string | null) => void;
  isLocked: boolean;
}) {
  const statDrive = isStatDrive(line.drive as LineDrive);

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Line
          </Label>
          <div className="text-sm font-medium mt-1">{line.lineTypeName}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {line.lineTypeCode}
          </div>
          {lineType?.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {lineType.description}
            </p>
          )}
        </div>

        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Drive
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            How the flow on this line is determined.
          </p>
          <Select
            value={line.drive}
            onValueChange={(value) =>
              onUpdate({ drive: value as FermLineDriveEnum })
            }
            disabled={isLocked}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DRIVE_ORDER.map((drive) => (
                <SelectItem key={drive} value={drive}>
                  <div className="flex flex-col items-start">
                    <span>{LINE_DRIVE_LABELS[drive]}</span>
                    <SelectItemDescription>
                      {LINE_DRIVE_DESCRIPTIONS[drive]}
                    </SelectItemDescription>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Which setpoint a stat line is controlled from is a consequence of
            the drive, not a separate choice: PH_STAT means ph_sp, DO_STAT
            means do_sp. It used to be a picker writing a column no reader ever
            consulted. */}
        {statDrive && (
          <div>
            <Label className="text-xs uppercase text-muted-foreground font-medium">
              Controlled From
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              The controller drives this line from{" "}
              <code className="bg-muted px-1 py-0.5 text-muted-foreground">
                {statSetpointCodeForDrive(line.drive as LineDrive)}
              </code>
              . Author that setpoint as a fermentation parameter; the line
              itself carries no rate.
            </p>
          </div>
        )}

        <div className="border-t pt-4 space-y-4">
          <div>
            <Label className="text-xs uppercase text-muted-foreground font-medium">
              Default Material
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Seeds the container form. The addition stays authoritative — a
              refill can legitimately differ.
            </p>
            <ResourceSelect
              value={line.default_resource_id}
              onChange={(value) => onUpdate({ defaultResourceId: value })}
              disabled={isLocked}
              categoryId={lineType?.feed_resource_category_id ?? null}
              placeholder="No default"
            />
          </div>

          {lineType?.has_concentration && (
            <div>
              <Label className="text-xs uppercase text-muted-foreground font-medium">
                Default Concentration
              </Label>
              <div className="mt-1 max-w-40">
                <ConcentrationField
                  value={line.default_concentration_pct}
                  onCommit={(value) =>
                    onUpdate({ defaultConcentrationPct: value })
                  }
                  disabled={isLocked}
                />
              </div>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Weight Unit
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            How this line's bottle weights are shown and entered in the
            journal. Follows the procedure unless overridden here.
          </p>
          <Select
            value={weightUnit ?? "__default__"}
            onValueChange={(value) =>
              onWeightUnitChange(value === "__default__" ? null : value)
            }
            disabled={isLocked}
          >
            <SelectTrigger className="mt-1 max-w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">
                Procedure default ({procedureWeightUnit})
              </SelectItem>
              {UNIT_CATEGORIES.additionWeight.allowedUnits.map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-t pt-4">
          <div className="border bg-muted/20 px-3 py-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="is-optional"
                checked={line.is_optional}
                onCheckedChange={(checked) =>
                  onUpdate({ isOptional: !!checked })
                }
                disabled={isLocked}
              />
              <Label htmlFor="is-optional" className="text-sm font-medium">
                Optional Line
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              A declared line is expected on every run of this procedure.
              Tick this for one that may legitimately be absent.
            </p>
          </div>
        </div>

        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Notes
          </Label>
          <Textarea
            defaultValue={line.notes ?? ""}
            onBlur={(e) => {
              const next = e.target.value;
              if (next !== (line.notes ?? "")) {
                onUpdate({ notes: next || null });
              }
            }}
            placeholder="Anything an operator should know about this line…"
            rows={3}
            className="mt-2 text-sm"
            readOnly={isLocked}
          />
        </div>
      </div>
    </ScrollArea>
  );
}

/**
 * Commits on blur rather than per keystroke — the field writes straight to the
 * server and a mutation per character is not worth it. NumericInput keeps the
 * in-progress text itself, so no mirrored state is needed here.
 */
function ConcentrationField({
  value,
  onCommit,
  disabled,
}: {
  value: string | null;
  onCommit: (value: string | null) => void;
  disabled: boolean;
}) {
  const draft = useRef<string | null>(value);

  return (
    <NumericInput
      precise
      value={value}
      onValueChange={(next) => {
        draft.current = next;
      }}
      onBlur={() => {
        if (draft.current !== value) onCommit(draft.current);
      }}
      postfix="%"
      placeholder="—"
      disabled={disabled}
    />
  );
}
